/**
 * <product-info>: product page behaviour.
 *  - Variant selection: radio inputs carry option value ids. On change the
 *    section is re-rendered server-side with `option_values` (Section
 *    Rendering API) and every [data-product-region] is swapped, so price,
 *    availability, picker states and gallery always come from Liquid.
 *  - Add to bag: submits [data-product-form] through Theme.cart.
 *  - Mobile gallery: keeps the pill indicators in sync with scroll.
 *  - "Write a review" opens the review app form (idempotent).
 */

const { cart, config, announce } = window.Theme;

class ProductInfo extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.addEventListener('change', (event) => this.onOptionChange(event));
    this.addEventListener('submit', (event) => this.onSubmit(event));
    this.addEventListener('click', (event) => this.onDotClick(event));
    this.bindGallery();
    this.bindStickyColumn();
    this.querySelectorAll('[data-product-form]').forEach((form) => this.guardBuyButton(form));
  }

  disconnectedCallback() {
    this.stickyObserver?.disconnect();
    this.buttonObservers?.forEach((observer) => observer.disconnect());
  }

  /* ---------------- Sticky info column ---------------- */

  /** Only pin the info column when it fits the viewport; otherwise its bottom would be unreachable. */
  bindStickyColumn() {
    const column = this.querySelector('[data-sticky-column]');
    if (!column) return;
    const update = () => {
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      const available = window.innerHeight - header * 16 - 48;
      column.toggleAttribute('data-fits', column.offsetHeight <= available);
    };
    this.stickyObserver = new ResizeObserver(update);
    this.stickyObserver.observe(column);
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  /* ---------------- Variants ---------------- */

  onOptionChange(event) {
    if (!event.target.matches('[data-option-value-id]')) return;
    const ids = [...this.querySelectorAll('[data-option-value-id]:checked')].map((input) => input.value);
    this.renderVariant(ids, event.target.id);
  }

  async renderVariant(optionValueIds, focusId) {
    this.controller?.abort();
    this.controller = new AbortController();
    this.setAttribute('aria-busy', 'true');

    const url = new URL(this.dataset.url, window.location.origin);
    url.searchParams.set('option_values', optionValueIds.join(','));
    url.searchParams.set('section_id', this.sectionId);

    try {
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error(`Variant render failed: ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

      this.querySelectorAll('[data-product-region]').forEach((region) => {
        const next = doc.querySelector(`[data-product-region="${region.dataset.productRegion}"]`);
        if (!next) return;
        if (region.querySelector('[data-product-form]')) this.updateBuyForm(region, next);
        else region.replaceWith(next);
      });
      this.bindGallery();
      if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });

      const variantId = this.querySelector('[data-variant-id]')?.value;
      if (variantId) {
        const pageUrl = new URL(window.location.href);
        pageUrl.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', pageUrl);
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * Update the add-to-bag form in place rather than replacing it: apps such as
   * Timesact (pre-orders) keep references to the form and its submit button and
   * re-apply their changes when the ?variant= URL changes.
   */
  updateBuyForm(region, next) {
    const form = region.querySelector('[data-product-form]');
    const nextForm = next.querySelector('[data-product-form]');
    if (!nextForm) return region.replaceWith(next);

    const id = form.querySelector('[data-variant-id]');
    const nextId = nextForm.querySelector('[data-variant-id]');
    if (id && nextId) {
      id.value = nextId.value;
      id.disabled = nextId.disabled;
    }

    const button = form.querySelector('[name="add"]');
    const nextButton = nextForm.querySelector('[name="add"]');
    if (button && nextButton) {
      button.buyState = { html: nextButton.innerHTML, disabled: nextButton.disabled };
      button.innerHTML = nextButton.innerHTML;
      button.disabled = nextButton.disabled;
    }

    const installments = form.querySelector('[data-installments]');
    const nextInstallments = nextForm.querySelector('[data-installments]');
    if (installments && nextInstallments) installments.replaceWith(nextInstallments);
  }

  /**
   * Apps rewrite the submit button (Timesact resets it to its own "Add to cart"
   * text, back-in-stock widgets re-enable sold-out buttons). Keep the theme's
   * label and state, unless an app has put the variant on a selling plan
   * (pre-order), in which case the app owns the button.
   */
  guardBuyButton(form) {
    const button = form.querySelector('[name="add"]');
    if (!button) return;
    button.buyState = { html: button.innerHTML, disabled: button.disabled };

    const observer = new MutationObserver(() => {
      const state = button.buyState;
      if (form.querySelector('[name="selling_plan"]')?.value) return;
      if (button.innerHTML !== state.html) button.innerHTML = state.html;
      if (button.disabled !== state.disabled && !button.hasAttribute('aria-busy')) button.disabled = state.disabled;
    });
    observer.observe(button, { attributes: true, attributeFilter: ['disabled'], childList: true, characterData: true, subtree: true });
    (this.buttonObservers ??= []).push(observer);
  }

  /* ---------------- Add to bag ---------------- */

  async onSubmit(event) {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    const error = form.querySelector('[data-product-error]');
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    if (error) error.textContent = '';

    try {
      await cart.add(new FormData(form));
      if (config.cartType === 'page') {
        window.location.href = config.routes.cart;
        return;
      }
    } catch (err) {
      if (error) error.textContent = err.message || config.strings.cartError;
      announce(err.message || config.strings.cartError);
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  /* ---------------- Mobile gallery ---------------- */

  bindGallery() {
    this.track = this.querySelector('[data-gallery-track]');
    this.dots = [...this.querySelectorAll('[data-gallery-dot]')];
    if (!this.track || !this.dots.length) return;

    this.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          const slide = this.track.firstElementChild;
          if (!slide) return;
          const index = Math.round(this.track.scrollLeft / (slide.offsetWidth + 4));
          this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
        });
      },
      { passive: true },
    );
  }

  onDotClick(event) {
    const dot = event.target.closest('[data-gallery-dot]');
    if (!dot || !this.track) return;
    const slide = this.track.children[Number(dot.dataset.galleryDot)];
    slide?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }
}

if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

/* "Write a review": open the reviews widget and trigger the review app's form. */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-write-review]');
  if (!trigger) return;
  const widget = document.getElementById('ReviewsWidget');
  if (!widget) return;
  event.preventDefault();
  widget.open = true;
  widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
  // Judge.me initializes its widget asynchronously; wait for the write link.
  let tries = 0;
  const openForm = () => {
    const link = widget.querySelector('.jdgm-write-rev-link');
    const ready = link && getComputedStyle(link).display !== 'none';
    if (ready) {
      // Judge.me's link toggles the form; only click when it's closed.
      const form = widget.querySelector('.jdgm-form-wrapper');
      if (!form || getComputedStyle(form).display === 'none') link.click();
      (form || widget).scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (++tries < 40) setTimeout(openForm, 150);
  };
  openForm();
});
